// Tiny HTTP client wrapper around fetch().
// One place for base URL, headers, error shaping, JSON parsing — so every
// screen that talks to the backend uses the same conventions.

import type { AppSettings, AssetSlot, Client, ClientService } from '../types';

// Vite exposes import.meta.env.VITE_* at build time. Empty string ⇒ same origin.
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// Git SHA this UI bundle was built from, baked in at build time. Falls back to
// "dev" for local development so the version chip is never empty.
export const UI_COMMIT = import.meta.env.VITE_BUILD_COMMIT || 'dev';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
      credentials: 'include',
    });
  } catch (err) {
    // Network-level failure (server down, DNS, CORS preflight rejected, …).
    throw new ApiError(0, url, `network error: ${(err as Error).message}`);
  }

  if (!res.ok) {
    // Try to lift the server-side {error: "..."} message before falling back
    // to a raw body / status text. Lets the UI show "tenant already exists"
    // instead of "Conflict".
    const body = await res.text().catch(() => '');
    let msg = body || res.statusText;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.error === 'string') msg = parsed.error;
    } catch {/* not JSON, keep raw */}
    throw new ApiError(res.status, url, msg);
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Tenants ─────────────────────────────────────────────────────────────────

export interface CreateTenantRequest {
  clientId: string;
  displayName: string;
  plan: string;
  contactEmail: string;
  branch?: string;
  services?: string[];
}

export interface UpdateTenantRequest {
  displayName?: string;
  plan?: string;
  status?: string;
  contactEmail?: string;
  branch?: string;
}

// fetchTenant loads one tenant with its current services + overlay. Used by
// the live polling loop on the client detail screen so CPU/mem metrics
// refresh without reloading the whole tenants list.
export function fetchTenant(clientId: string): Promise<Client> {
  return request<Client>(`/api/v1/tenants/${encodeURIComponent(clientId)}`);
}

export function fetchTenants(): Promise<Client[]> {
  return request<Client[]>('/api/v1/tenants');
}

export function createTenant(req: CreateTenantRequest): Promise<Client> {
  return request<Client>('/api/v1/tenants', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function updateTenant(clientId: string, patch: UpdateTenantRequest): Promise<Client> {
  return request<Client>(`/api/v1/tenants/${encodeURIComponent(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteTenant(clientId: string): Promise<void> {
  return request<void>(`/api/v1/tenants/${encodeURIComponent(clientId)}`, {
    method: 'DELETE',
  });
}

// fetchDeletedTenants returns soft-deleted tenants for the trash screen.
// The wire records carry deletedAt + purgeAt; live fields like status are
// frozen at the moment of deletion and not refreshed against k8s.
export function fetchDeletedTenants(): Promise<Client[]> {
  return request<Client[]>('/api/v1/tenants/deleted');
}

// recoverTenant reverses a soft-delete within the 30-day window. The BE
// re-renders the tenant into git (namespace + Apps) so ArgoCD picks it
// back up — to the user it looks like the tenant just came back.
export function recoverTenant(clientId: string): Promise<Client> {
  return request<Client>(`/api/v1/tenants/${encodeURIComponent(clientId)}/recover`, {
    method: 'POST',
  });
}

// purgeTenant permanently deletes a soft-deleted tenant, skipping the 30-day
// auto-purge wait. Irreversible: the DB row (and its cascaded services) are
// hard-deleted and the cluster/git side-effects are re-run. 204 on success.
export function purgeTenant(clientId: string): Promise<void> {
  return request<void>(`/api/v1/tenants/${encodeURIComponent(clientId)}/purge`, {
    method: 'DELETE',
  });
}

// purgeAllDeleted permanently purges every soft-deleted tenant in one request
// (server-side loop, so it isn't throttled by the per-request rate limiter).
export function purgeAllDeleted(): Promise<{ purged: number; failed: number }> {
  return request<{ purged: number; failed: number }>(`/api/v1/tenants/deleted`, {
    method: 'DELETE',
  });
}

// ── Admin: hot-reload the chart catalog ────────────────────────────────────

export interface ReloadChartsResponse {
  loaded: number;
  services: string[];
  gitPulled: boolean;
  errors?: Array<{ path: string; error: string }>;
}

// reloadCharts triggers a server-side git pull + chart-meta re-scan + live
// catalog swap. Used after pushing a new chart-meta.yaml to the GitOps repo
// when the operator doesn't want to bounce the BE.
export function reloadCharts(): Promise<ReloadChartsResponse> {
  return request<ReloadChartsResponse>('/api/v1/admin/reload-charts', {
    method: 'POST',
  });
}

// ── Cluster events ─────────────────────────────────────────────────────────

export interface ClusterEvent {
  lastSeen: string;   // RFC3339
  type: string;       // Normal | Warning
  reason: string;     // FailedScheduling, ImagePullBackOff, Killing, ...
  object: string;     // "<kind>/<name>"
  namespace: string;
  tenant: string;     // derived from namespace ("tenant-<id>" → "<id>") or ""
  message: string;
  count: number;
  source: string;     // reporting component (kubelet, default-scheduler, ...)
}

export interface FetchEventsOpts {
  namespace?: string;       // "" = all ns
  tenant?: string;          // shortcut for namespace="tenant-<id>"
  limit?: number;           // default 200, max 1000
  warningsOnly?: boolean;
}

// ── GitOps commit history per tenant ──────────────────────────────────────

export interface GitOpsCommit {
  sha: string;
  short: string;
  author: string;
  email: string;
  message: string;
  time: string;        // RFC3339
  browseUrl?: string;  // empty when the repo URL isn't a recognised GitLab/GitHub
}

export interface GitOpsHistoryResponse {
  enabled: boolean;
  commits: GitOpsCommit[];
  error?: string;
}

export function fetchGitOpsHistory(clientId: string, limit = 50): Promise<GitOpsHistoryResponse> {
  return request<GitOpsHistoryResponse>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/gitops-history?limit=${limit}`,
  );
}

export function fetchEvents(opts: FetchEventsOpts = {}): Promise<ClusterEvent[]> {
  const q = new URLSearchParams();
  if (opts.namespace) q.set('ns', opts.namespace);
  if (opts.tenant)    q.set('tenant', opts.tenant);
  if (opts.limit)     q.set('limit', String(opts.limit));
  if (opts.warningsOnly) q.set('type', 'Warning');
  const qs = q.toString();
  return request<ClusterEvent[]>('/api/v1/events' + (qs ? '?' + qs : ''));
}

// ── Tenant services ────────────────────────────────────────────────────────

export interface UpdateServiceRequest {
  status?: string;        // pending | healthy | degraded | failing
  replicas?: number;      // 0–100
  cpuLimit?: string;      // k8s style: "500m", "2"
  memLimit?: string;      // k8s style: "2Gi", "512Mi"
  nodeName?: string;
  imageTag?: string;      // image tag override, e.g. "v1.0.1"; "" clears it (chart default)
  // Per-service selected asset versions (assetKey -> version). Replaces the
  // whole selection; an empty object clears it. Each key must be a slot the
  // service declares; each version must be an existing published version.
  assetVersions?: Record<string, string>;
}

export function updateService(clientId: string, serviceName: string, patch: UpdateServiceRequest): Promise<ClientService> {
  return request<ClientService>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
}

// ImageTags is the response of the image-tags endpoint. `supported` is false
// when the image is unknown, its registry isn't listable, or a provider error
// occurred — the UI then keeps the plain free-text image-version input.
export interface ImageTags {
  supported: boolean;
  registry?: string;  // source registry host, present when supported
  tags?: string[];    // up to ~100 tags, newest-first
}

// fetchServiceImageTags lists selectable image versions for a service. The
// endpoint always returns 200 (it degrades to {supported:false} on any error),
// so callers can treat a thrown error the same as unsupported.
export function fetchServiceImageTags(clientId: string, serviceName: string): Promise<ImageTags> {
  return request<ImageTags>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/image-tags`,
  );
}

// ── Service assets (versioned, client-uploadable files) ────────────────────

// AssetVersion is the metadata for one published version (NO content — listings
// never return the bytes).
export interface AssetVersion {
  version: string;
  filename: string;
  createdAt: string;   // RFC3339
  createdBy?: string;
}

// listAssetVersions returns the published versions for a service's asset slot.
export function listAssetVersions(clientId: string, serviceName: string, assetKey: string): Promise<AssetVersion[]> {
  return request<AssetVersion[]>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/assets/${encodeURIComponent(assetKey)}/versions`,
  );
}

export interface CreateAssetVersionRequest {
  version: string;    // immutable; re-publishing an existing name is a 409
  filename?: string;  // defaults to the slot's contract filename
  content: string;    // the file text (e.g. workflow JSON)
}

// uploadAssetVersion publishes a new immutable version. Throws ApiError (409)
// when the version name already exists.
export function uploadAssetVersion(clientId: string, serviceName: string, assetKey: string, req: CreateAssetVersionRequest): Promise<AssetVersion> {
  return request<AssetVersion>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/assets/${encodeURIComponent(assetKey)}/versions`,
    { method: 'POST', body: JSON.stringify(req) },
  );
}

// deleteAssetVersion removes a version. Throws ApiError (409) when a service
// currently selects it.
export function deleteAssetVersion(clientId: string, serviceName: string, assetKey: string, version: string): Promise<void> {
  return request<void>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/assets/${encodeURIComponent(assetKey)}/versions/${encodeURIComponent(version)}`,
    { method: 'DELETE' },
  );
}

export function restartService(clientId: string, serviceName: string): Promise<ClientService> {
  return request<ClientService>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/restart`,
    { method: 'POST' },
  );
}

export function setServiceIngress(
  clientId: string,
  serviceName: string,
  ingressHost: string,
  tlsEnabled: boolean,
): Promise<ClientService> {
  return request<ClientService>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/ingress`,
    { method: 'PUT', body: JSON.stringify({ ingressHost, tlsEnabled }) },
  );
}

export function clearServiceIngress(clientId: string, serviceName: string): Promise<ClientService> {
  return request<ClientService>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/ingress`,
    { method: 'DELETE' },
  );
}

export interface AddServiceRequest {
  service: string;             // catalog key, e.g. "grafana" | "loki"
  replicas?: number;
  cpuLimit?: string;
  memLimit?: string;
}

export function addService(clientId: string, req: AddServiceRequest): Promise<ClientService> {
  return request<ClientService>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services`,
    { method: 'POST', body: JSON.stringify(req) },
  );
}

// deleteService removes one service from a tenant. GitOps re-renders without it
// and ArgoCD prunes the workload.
export function deleteService(clientId: string, serviceName: string): Promise<void> {
  return request<void>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}`,
    { method: 'DELETE' },
  );
}

// fetchServiceLogs returns the raw text payload from BE's logs endpoint
// (Content-Type: text/plain). Uses fetch directly because `request<T>` is
// JSON-typed.
export async function fetchServiceLogs(clientId: string, serviceName: string, tail = 200): Promise<string> {
  // Same BASE_URL prefix as the typed request<T> helper — without it, in dev
  // mode the path resolves to Vite (5173) instead of the backend (8080) and
  // we get the SPA fallback (index.html) instead of real logs.
  const url = `${BASE_URL}/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/logs?tail=${tail}`;
  const res = await fetch(url, { credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    // BE returns JSON {"error":"..."} on failure but we typed this as text;
    // try to parse for a nicer message, fall back to raw body.
    let message = text || 'failed to fetch logs';
    try {
      const j = JSON.parse(text);
      if (j && typeof j.error === 'string') message = j.error;
    } catch { /* not JSON, use the raw text */ }
    throw new ApiError(res.status, url, message);
  }
  return text;
}

export function suspendTenant(clientId: string): Promise<Client> {
  return request<Client>(`/api/v1/tenants/${encodeURIComponent(clientId)}/suspend`, { method: 'POST' });
}

export function resumeTenant(clientId: string): Promise<Client> {
  return request<Client>(`/api/v1/tenants/${encodeURIComponent(clientId)}/resume`, { method: 'POST' });
}

// ── App settings ───────────────────────────────────────────────────────────

export function fetchSettings(): Promise<AppSettings> {
  return request<AppSettings>('/api/v1/settings');
}

export function updateSettings(patch: Partial<AppSettings>): Promise<AppSettings> {
  return request<AppSettings>('/api/v1/settings', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ── Plan-tier limits ───────────────────────────────────────────────────────

export const KnownPlans = ['starter', 'growth', 'scale', 'enterprise', 'unlimited'] as const;

export interface PlanLimits {
  plan: string;
  maxReplicas: number;
  maxMilliCPU: number;
  maxMemoryBytes: number;
  maxGPUMilliCPU: number;
  maxGPUMemoryBytes: number;
}

export function fetchPlanLimits(): Promise<PlanLimits[]> {
  return request<PlanLimits[]>('/api/v1/plans/limits');
}

export function updatePlanLimits(plan: string, body: Partial<Omit<PlanLimits, 'plan'>>): Promise<PlanLimits> {
  return request<PlanLimits>(`/api/v1/plans/limits/${plan}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ── Cluster nodes ───────────────────────────────────────────────────────────

export interface NodeStatus {
  name: string;
  roles: string[];
  ready: boolean;
  cpuPercent: number;        // -1 = no metrics
  memPercent: number;
  gpuCapacity: number;         // 0 = no GPU
  gpuPercent: number;          // allocated GPU %, -1 when no GPU
  gpuConfiguredSlots: number;  // time-slicing slots last set (from node label); 0 = unset
  gpuUtilPercent: number;      // real compute util % (DCGM), -1 = n/a
  gpuMemUsedBytes: number;     // summed GPU VRAM used (DCGM)
  gpuMemCapacityBytes: number; // summed GPU VRAM total (DCGM)
  diskPercent: number;       // rootfs used %, -1 if unavailable
  diskBytes: number;         // rootfs usedBytes
  diskCapacityBytes: number; // rootfs capacityBytes
  podCount: number;
  podCapacity: number;
  taints: string[];
  architecture: string;
  kubeletVersion: string;
  osImage: string;
  cordoned: boolean;
}

export function fetchNodes(): Promise<NodeStatus[]> {
  return request<NodeStatus[]>('/api/v1/nodes');
}

// NodeMetrics is the expensive, lazily-fetched slice of per-node stats (real
// GPU util+VRAM via DCGM, rootfs disk via kubelet stats) — split off the node
// list so the overview renders immediately instead of blocking on a per-node
// probe that might hang.
export interface NodeMetrics {
  gpuUtilPercent: number;      // avg compute util %, -1 = n/a
  gpuMemUsedBytes: number;
  gpuMemCapacityBytes: number;
  gpuCount: number;            // physical GPUs DCGM sees (capacity fallback)
  diskPercent: number;         // rootfs used %, -1 = n/a
  diskBytes: number;
  diskCapacityBytes: number;
}

// fetchNodeMetrics returns the lazily-loaded per-node metrics keyed by node
// name. Nodes with nothing to report are absent from the map.
export function fetchNodeMetrics(): Promise<Record<string, NodeMetrics>> {
  return request<Record<string, NodeMetrics>>('/api/v1/nodes/metrics');
}

// mergeNodeMetrics overlays the lazily-fetched metrics onto the fast node list,
// returning a new array. Nodes without a metrics entry keep their -1/0
// "loading" cells (rendered as dashes).
export function mergeNodeMetrics(nodes: NodeStatus[], metrics: Record<string, NodeMetrics>): NodeStatus[] {
  return nodes.map((n) => {
    const m = metrics[n.name];
    if (!m) return n;
    return {
      ...n,
      gpuUtilPercent: m.gpuUtilPercent,
      gpuMemUsedBytes: m.gpuMemUsedBytes,
      gpuMemCapacityBytes: m.gpuMemCapacityBytes,
      // Node advertises no allocatable GPU but DCGM sees physical GPUs → surface those.
      gpuCapacity: n.gpuCapacity > 0 ? n.gpuCapacity : m.gpuCount,
      diskPercent: m.diskPercent,
      diskBytes: m.diskBytes,
      diskCapacityBytes: m.diskCapacityBytes,
    };
  });
}

export interface PodInfo {
  name: string;
  namespace: string;
  tenant: string;   // "" for system pods
  phase: string;    // Running | Pending | ...
  ready: string;    // "1/1"
  restarts: number;
  age: string;      // "3h"
}

export function fetchNodePods(node: string): Promise<PodInfo[]> {
  return request<PodInfo[]>(`/api/v1/nodes/${encodeURIComponent(node)}/pods`);
}

// setNodeGPUSlots configures GPU time-slicing on a node — how many pods may
// share one physical GPU (1 = exclusive).
export function setNodeGPUSlots(node: string, slots: number): Promise<{ node: string; slots: number }> {
  return request(`/api/v1/nodes/${encodeURIComponent(node)}/gpu-slots`, {
    method: 'POST',
    body: JSON.stringify({ slots }),
  });
}

export interface ChartCatalogEntry {
  name: string;
  label: string;
  chartName: string;
  chartVersion: string;
  chartPath: string;
  kind: string;
  icon: string;
  group: string;
  defaultReplicas: number;
  defaultCpu: string;
  defaultMem: string;
  assets?: AssetSlot[]; // declared uploadable, versioned file slots (empty/absent = none)
}

export function fetchChartCatalog(): Promise<ChartCatalogEntry[]> {
  return request<ChartCatalogEntry[]>('/api/v1/chart-catalog');
}

export interface LiveConfig {
  chartBacked: boolean;
  yaml: string;
  filename?: string;
  applicationName?: string;
  note?: string;
}

export function fetchLiveConfig(clientId: string, serviceName: string): Promise<LiveConfig> {
  return request<LiveConfig>(
    `/api/v1/tenants/${encodeURIComponent(clientId)}/services/${encodeURIComponent(serviceName)}/live-config`,
  );
}

export interface ArgocdStatus {
  sync: string;     // Synced | OutOfSync | Unknown
  health: string;   // Healthy | Degraded | Progressing | ...
  revision?: string;
  exists: boolean;
  error?: string;
}

export function fetchArgocdStatus(clientId: string): Promise<ArgocdStatus> {
  return request<ArgocdStatus>(`/api/v1/tenants/${encodeURIComponent(clientId)}/argocd-status`);
}

export interface PVCUsage {
  name: string;
  usedBytes: number;
  capacityBytes: number;
}

export function fetchPVCUsage(clientId: string): Promise<PVCUsage[]> {
  return request<PVCUsage[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/pvc-usage`);
}

// ── Basic-auth user mappings (BasicUser CRDs) ──────────────────────────────
export interface BasicUser {
  username: string;
  clientId: string;
  title?: string;
  note?: string;
}

export function fetchBasicUsers(clientId: string): Promise<BasicUser[]> {
  return request<BasicUser[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth`);
}

export function createBasicUser(clientId: string, username: string, password: string, title?: string, note?: string): Promise<BasicUser> {
  return request<BasicUser>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth`, {
    method: 'POST',
    body: JSON.stringify({ username, password, title: title ?? '', note: note ?? '' }),
  });
}

export function deleteBasicUser(clientId: string, username: string): Promise<void> {
  return request<void>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  });
}

// updateBasicUserNote sets (or clears, when empty) the short title + longer
// note on a BasicUser CR. Returns the updated record.
export function updateBasicUserNote(clientId: string, username: string, title: string, note: string): Promise<BasicUser> {
  return request<BasicUser>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth/${encodeURIComponent(username)}/note`, {
    method: 'PUT',
    body: JSON.stringify({ title, note }),
  });
}

// updateBasicUserPassword rotates a BasicUser's password (write-only).
export function updateBasicUserPassword(clientId: string, username: string, password: string): Promise<BasicUser> {
  return request<BasicUser>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth/${encodeURIComponent(username)}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}

// ── S3 connections (per-tenant) ────────────────────────────────────────────
export interface S3Config {
  id: string;
  name: string;
  /** Free text the operator can change any time. On a managed bucket it is the
   *  only editable label — `name` seeded the real bucket name and is frozen. */
  note: string;
  endpoint: string;
  accessKey: string;
  secretKey: string;
  region: string;
  bucket: string;
  /** true = bucket + user + quota provisioned by us on the platform's MinIO. */
  managed: boolean;
  /** Hard bucket quota in bytes; 0 = unlimited. Managed connections only. */
  quotaBytes: number;
  /** The server withholds a managed bucket's secret after it is shown once. */
  secretHidden: boolean;
  /** Registered storage server the bucket lives on; absent = the one this
   *  deployment configures in its environment. */
  minioServerId?: string;
  minioServerName?: string;
  createdAt: string;
  updatedAt: string;
}

// The mutable fields of an S3 connection (everything except server-set ids/timestamps).
export type S3ConfigInput = Pick<S3Config, 'name' | 'note' | 'endpoint' | 'accessKey' | 'secretKey' | 'region' | 'bucket'>;

export function fetchS3Configs(clientId: string): Promise<S3Config[]> {
  return request<S3Config[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3`);
}

export function createS3Config(clientId: string, input: S3ConfigInput): Promise<S3Config> {
  return request<S3Config>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateS3Config(clientId: string, id: string, input: S3ConfigInput): Promise<S3Config> {
  return request<S3Config>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function deleteS3Config(clientId: string, id: string): Promise<void> {
  return request<void>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Managed buckets (provisioned on the platform's MinIO) ──────────────────

/**
 * The credentials for a managed bucket, delivered exactly once — by
 * provisionS3Bucket at creation and by rotateS3BucketKeys afterwards. The
 * secret is never returned by any read endpoint, so if this is dropped without
 * being copied it can only be replaced, not recovered.
 */
export interface ProvisionedBucket {
  connection: S3Config;
  accessKey: string;
  secretKey: string;
  endpoint: string;
  bucket: string;
  region: string;
}

/** Live consumption of a managed bucket, read from the storage server. */
export interface BucketUsage {
  bytes: number;
  objects: number;
  quota: number;
  /**
   * True while uploads into this bucket are switched off for being over the
   * size limit. Reads and deletes still work — deleting is how a client gets
   * back under the limit and has its uploads restored automatically.
   */
  writesPaused: boolean;
}

/** Creates a real bucket + a user scoped to it + an optional quota. */
// serverId picks the registered storage server to create the bucket on; the
// backend treats an absent or empty value as "the one configured by env".
export function provisionS3Bucket(clientId: string, input: { name: string; note?: string; quotaBytes: number; serverId?: string }): Promise<ProvisionedBucket> {
  return request<ProvisionedBucket>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/provision`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function fetchBucketUsage(clientId: string, id: string): Promise<BucketUsage> {
  return request<BucketUsage>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}/usage`);
}

/** Applies a hard bucket quota; 0 = unlimited. */
export function setBucketQuota(clientId: string, id: string, quotaBytes: number): Promise<S3Config> {
  return request<S3Config>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}/quota`, {
    method: 'PUT',
    body: JSON.stringify({ quotaBytes }),
  });
}

/**
 * Reveals one connection's endpoint + access key + secret key. Separate from
 * the list endpoint on purpose: the secret travels only when it's asked for,
 * and the server records an audit event for the reveal.
 */
export function fetchS3Credentials(clientId: string, id: string): Promise<ProvisionedBucket> {
  return request<ProvisionedBucket>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}/credentials`);
}

/** Issues a new secret key; the previous one stops working immediately. */
export function rotateS3BucketKeys(clientId: string, id: string): Promise<ProvisionedBucket> {
  return request<ProvisionedBucket>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(id)}/rotate-keys`, {
    method: 'POST',
  });
}

// ── Basic-auth user → S3 access grants ─────────────────────────────────────

// One grant: which connection, and read and/or write. A grant only exists when
// at least one of canRead/canWrite is true.
export interface S3Grant {
  s3ConfigId: string;
  canRead: boolean;
  canWrite: boolean;
}

export function fetchBasicUserS3Grants(clientId: string, username: string): Promise<S3Grant[]> {
  return request<S3Grant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth/${encodeURIComponent(username)}/s3-grants`);
}

// setBasicUserS3Grants replaces the user's whole grant set (server drops entries
// with neither read nor write).
export function setBasicUserS3Grants(clientId: string, username: string, grants: S3Grant[]): Promise<S3Grant[]> {
  return request<S3Grant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/basic-auth/${encodeURIComponent(username)}/s3-grants`, {
    method: 'PUT',
    body: JSON.stringify(grants),
  });
}

// The reverse direction: a grant from the connection's side — which user + r/w.
export interface S3ConfigGrant {
  username: string;
  canRead: boolean;
  canWrite: boolean;
}

export function fetchS3ConfigGrants(clientId: string, configId: string): Promise<S3ConfigGrant[]> {
  return request<S3ConfigGrant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(configId)}/grants`);
}

// setS3ConfigGrants replaces which users have access to one connection.
export function setS3ConfigGrants(clientId: string, configId: string, grants: S3ConfigGrant[]): Promise<S3ConfigGrant[]> {
  return request<S3ConfigGrant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(configId)}/grants`, {
    method: 'PUT',
    body: JSON.stringify(grants),
  });
}

// Per basic-auth-user permission to SEE this tenant's services in the client
// portal (/services-lookup). Tenant-wide (not per S3 connection). Users absent
// from the list default to allowed.
export interface ServiceAccessEntry {
  username: string;
  canView: boolean;
}

export function fetchServiceAccess(clientId: string): Promise<ServiceAccessEntry[]> {
  return request<ServiceAccessEntry[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/service-access`);
}

export function setServiceAccess(clientId: string, entries: ServiceAccessEntry[]): Promise<ServiceAccessEntry[]> {
  return request<ServiceAccessEntry[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/service-access`, {
    method: 'PUT',
    body: JSON.stringify(entries),
  });
}

// Set ONE user's service visibility without touching other users (per-user editor).
export function setUserServiceAccess(clientId: string, username: string, canView: boolean): Promise<ServiceAccessEntry> {
  return request<ServiceAccessEntry>(`/api/v1/tenants/${encodeURIComponent(clientId)}/service-access/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify({ canView }),
  });
}

// Grants for EXTERNAL s3_accounts on one connection (parallel to the user
// grants above, keyed by the global account id instead of a basic-auth username).
export interface S3ConfigAccountGrant {
  s3AccountId: string;
  canRead: boolean;
  canWrite: boolean;
}

export function fetchS3ConfigAccountGrants(clientId: string, configId: string): Promise<S3ConfigAccountGrant[]> {
  return request<S3ConfigAccountGrant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(configId)}/account-grants`);
}

export function setS3ConfigAccountGrants(clientId: string, configId: string, grants: S3ConfigAccountGrant[]): Promise<S3ConfigAccountGrant[]> {
  return request<S3ConfigAccountGrant[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/s3/${encodeURIComponent(configId)}/account-grants`, {
    method: 'PUT',
    body: JSON.stringify(grants),
  });
}

// ── Tenant bulk import ─────────────────────────────────────────────────────

export interface BulkImportResult {
  total: number;
  created: number;
  results: { clientId: string; status: string; error?: string }[];
}

export function bulkImportTenants(tenants: Array<Partial<CreateTenantRequest> & { clientId: string }>): Promise<BulkImportResult> {
  return request<BulkImportResult>('/api/v1/tenants/import', {
    method: 'POST',
    body: JSON.stringify({ tenants }),
  });
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export interface DashboardCounts {
  total: number;
  ready?: number;
  healthy?: number;
  degraded: number;
  failing: number;
  provisioning?: number;
  pending?: number;
  suspended?: number;
}

export interface DashboardSummary {
  tenants: DashboardCounts;
  services: DashboardCounts;
  totalReplicas: number;
  totalCPUMilli: number;
  totalMemoryBytes: number;
}

export function fetchDashboard(): Promise<DashboardSummary> {
  return request<DashboardSummary>('/api/v1/dashboard');
}

// ── Live API metrics (RED) — point-in-time snapshot of the BE's own
// Prometheus registry. The dashboard polls it and derives req/s from the
// delta between samples.
export interface MetricsRouteStat {
  method: string;
  route: string;
  count: number;
  avgMs: number;
}
export interface MetricsSummary {
  totalRequests: number;
  byStatusClass: Record<string, number>; // "2xx","4xx","5xx"
  inFlight: number;
  avgLatencyMs: number;
  routes: MetricsRouteStat[];
}

export function fetchMetricsSummary(): Promise<MetricsSummary> {
  return request<MetricsSummary>('/api/v1/metrics-summary');
}

// ── Auth ───────────────────────────────────────────────────────────────────

export interface MeWire {
  id: string;
  email: string;
  displayName?: string;
}

export function login(email: string, password: string): Promise<MeWire> {
  return request<MeWire>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function logout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' });
}

// ── Public client-facing service lookup (BasicUser credentials) ──────────────
// No admin session — the client authenticates with the username/password from
// their BasicUser CR and gets back a lean list of their own services.
export interface PublicEndpoint {
  name: string;    // k8s Service name, e.g. "loki-memberlist"
  fqdn: string;    // <svc>.<ns>.svc.<clusterDomain>
  ports: number[]; // this Service's ports
}

export interface PublicService {
  name: string;
  status: string;
  endpoints: PublicEndpoint[]; // one per fronting k8s Service
  url?: string;                // external https?://<domain>; absent when no domain set
}

export interface PublicLookupResult {
  clientId: string;      // exact tenant id
  displayName?: string;  // friendly name when set
  services: PublicService[];
}

export function lookupServicesByBasicAuth(username: string, password: string): Promise<PublicLookupResult> {
  // HTTP Basic Auth so the same endpoint works from a plain `curl -u user:pass`.
  // btoa needs latin1, so UTF-8-encode first to survive non-ASCII credentials.
  const token = btoa(unescape(encodeURIComponent(`${username}:${password}`)));
  return request<PublicLookupResult>('/api/v1/public/services-fqdn', {
    headers: { Authorization: `Basic ${token}` },
  });
}

export function fetchMe(): Promise<MeWire> {
  return request<MeWire>('/api/v1/me');
}

// ── Audit log ──────────────────────────────────────────────────────────────

export interface AuditEvent {
  id: string;
  actorEmail: string;
  actorType: string;
  action: string;
  target: string;
  payload?: unknown;
  requestId?: string;
  createdAt: string; // RFC3339
}

export interface AuditFilter {
  actor?: string;
  target?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface AuditPage {
  items: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
}

// ── Bypass rules (admin) ───────────────────────────────────────────────────

export interface BypassRule {
  id: string;
  type: 'unknown' | 'client' | 'global';
  tenantId?: string;
  clientId?: string;
  regex: string;
  accessMode: 'r' | 'w';
  service?: string;
  createdAt: string;
  createdBy: string;
  createdVia: 'dashboard' | 'rest_api' | 'import';
  ownerTokenId?: string;    // owning access token, or absent = operator-owned
  ownerTokenLabel?: string; // resolved label of the owning token
}

export interface BypassRuleFilter {
  type?: 'unknown' | 'client' | 'global';
  clientId?: string;
  ownerTokenId?: string; // an access-token id, or 'none' for operator-owned rules
}

export interface CreateBypassRuleRequest {
  type: 'unknown' | 'client' | 'global';
  clientId?: string;
  regex: string;
  accessMode?: 'r' | 'w';
  service?: string;
  ownerTokenId?: string; // assign to an access token; omit/empty = operator-owned
}

export interface TransitionBypassRuleRequest {
  type: 'unknown' | 'client' | 'global';
  clientId?: string;
}

export function fetchBypassRules(f: BypassRuleFilter = {}): Promise<BypassRule[]> {
  const p = new URLSearchParams();
  if (f.type) p.set('type', f.type);
  if (f.clientId) p.set('clientId', f.clientId);
  if (f.ownerTokenId) p.set('ownerTokenId', f.ownerTokenId);
  const qs = p.toString();
  return request<BypassRule[]>(`/api/v1/bypass-rules${qs ? '?' + qs : ''}`);
}

export function createBypassRule(req: CreateBypassRuleRequest): Promise<BypassRule> {
  return request<BypassRule>('/api/v1/bypass-rules', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function transitionBypassRule(id: string, req: TransitionBypassRuleRequest): Promise<BypassRule> {
  return request<BypassRule>(`/api/v1/bypass-rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(req),
  });
}

export function deleteBypassRule(id: string): Promise<void> {
  return request<void>(`/api/v1/bypass-rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Reassign a rule's owning access token. Pass a token id to assign, or null/
// undefined to clear it back to operator-owned.
export function setBypassRuleOwner(id: string, ownerTokenId?: string | null): Promise<BypassRule> {
  return request<BypassRule>(`/api/v1/bypass-rules/${encodeURIComponent(id)}/owner`, {
    method: 'PUT',
    body: JSON.stringify({ ownerTokenId: ownerTokenId ?? null }),
  });
}

// ── Registries (managed pull-secret credentials) ───────────────────────────

// RegistryType labels the provider. All use the OCI v2 tag-listing path today;
// the field is a label + future-proofing.
export type RegistryType = 'harbor' | 'oci' | 'gitlab';

export interface Registry {
  id: string;
  name: string;
  url: string;
  username: string;
  password: string; // always "********" — write-only, never the real value
  email?: string;
  type: RegistryType;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRegistryRequest {
  name: string;
  url: string;
  username: string;
  password: string;
  email?: string;
  type?: RegistryType;
}

// UpdateRegistryRequest — all fields optional. Omit `password` (or send an
// empty string) to keep the existing one (it's write-only).
export interface UpdateRegistryRequest {
  name?: string;
  url?: string;
  username?: string;
  password?: string;
  email?: string;
  type?: RegistryType;
}

export function fetchRegistries(): Promise<Registry[]> {
  return request<Registry[]>('/api/v1/registries');
}

export function createRegistry(req: CreateRegistryRequest): Promise<Registry> {
  return request<Registry>('/api/v1/registries', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function updateRegistry(id: string, patch: UpdateRegistryRequest): Promise<Registry> {
  return request<Registry>(`/api/v1/registries/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteRegistry(id: string): Promise<void> {
  return request<void>(`/api/v1/registries/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── S3 accounts (global external users) ─────────────────────────────────────
// Identity-only external users that get granted read/write on individual
// tenants' S3 connections from the per-tenant S3 card. Their S3 keys live
// separately (Access tokens).

export interface S3Account {
  id: string;
  name: string;
  password: string; // demo-grade: returned decrypted, masked in the UI
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface S3AccountInput {
  name: string;
  password?: string;
  note?: string;
}

export function fetchS3Accounts(): Promise<S3Account[]> {
  return request<S3Account[]>('/api/v1/s3-accounts');
}

export function createS3Account(req: S3AccountInput): Promise<S3Account> {
  return request<S3Account>('/api/v1/s3-accounts', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function updateS3Account(id: string, patch: Partial<S3AccountInput>): Promise<S3Account> {
  return request<S3Account>(`/api/v1/s3-accounts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteS3Account(id: string): Promise<void> {
  return request<void>(`/api/v1/s3-accounts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Reverse view: everywhere an S3 account is granted read/write, across tenants.
export interface S3AccountAccess {
  clientId: string;
  connectionId: string;
  connectionName: string;
  bucket: string;
  canRead: boolean;
  canWrite: boolean;
}

export function fetchS3AccountAccess(id: string): Promise<S3AccountAccess[]> {
  return request<S3AccountAccess[]>(`/api/v1/s3-accounts/${encodeURIComponent(id)}/access`);
}

// ── API tokens (per tenant) ────────────────────────────────────────────────

export interface APIToken {
  id: string;
  label: string;
  expiresAt?: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface CreateAPITokenResponse extends APIToken {
  token: string; // plaintext, shown once
}

export function fetchAPITokens(clientId: string): Promise<APIToken[]> {
  return request<APIToken[]>(`/api/v1/tenants/${encodeURIComponent(clientId)}/api-tokens`);
}

export function createAPIToken(clientId: string, label: string, expiresIn?: string): Promise<CreateAPITokenResponse> {
  return request<CreateAPITokenResponse>(`/api/v1/tenants/${encodeURIComponent(clientId)}/api-tokens`, {
    method: 'POST',
    body: JSON.stringify({ label, expiresIn }),
  });
}

export function revokeAPIToken(clientId: string, id: string): Promise<void> {
  return request<void>(`/api/v1/tenants/${encodeURIComponent(clientId)}/api-tokens/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Access tokens (global, app-to-app) ──────────────────────────────────────
// Machine tokens for external apps to authenticate to this API. Management
// only for now — nothing enforces them yet.

export interface AccessToken {
  id: string;
  label: string;
  expiresAt?: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

export interface CreateAccessTokenResponse extends AccessToken {
  token: string; // plaintext, shown once
}

export function fetchAccessTokens(): Promise<AccessToken[]> {
  return request<AccessToken[]>('/api/v1/access-tokens');
}

export function createAccessToken(label: string, expiresIn?: string): Promise<CreateAccessTokenResponse> {
  return request<CreateAccessTokenResponse>('/api/v1/access-tokens', {
    method: 'POST',
    body: JSON.stringify({ label, expiresIn }),
  });
}

export function revokeAccessToken(id: string): Promise<void> {
  return request<void>(`/api/v1/access-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Audit ──────────────────────────────────────────────────────────────────

export function fetchAudit(f: AuditFilter = {}): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (f.actor) params.set('actor', f.actor);
  if (f.target) params.set('target', f.target);
  if (f.action) params.set('action', f.action);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.limit != null) params.set('limit', String(f.limit));
  if (f.offset != null) params.set('offset', String(f.offset));
  const qs = params.toString();
  return request<AuditPage>(`/api/v1/audit${qs ? '?' + qs : ''}`);
}

// ── Version ────────────────────────────────────────────────────────────────

export interface VersionInfo {
  commit: string;
  builtAt?: string;
}

// fetchVersion returns the backend build commit. Public endpoint (no auth), so
// it works on the login screen too. Never throws — a missing/unreachable BE
// yields null and the UI just shows "API ?".
export function fetchVersion(): Promise<VersionInfo | null> {
  return request<VersionInfo>('/api/v1/version').catch(() => null);
}

// ── Global services ────────────────────────────────────────────────────────

export interface GlobalService {
  name: string;
  chart: string;
  chartVersion: string;
  status: string;
  icon?: string;
  serviceGroup: string;
  replicas: number;
  cpuLimitMilli: number;
  memoryLimitBytes: number;
  nodeName?: string;
  imageTag?: string;
  ingressHost: string;
  tlsEnabled: boolean;
  diskBytes: number;
  diskCapacityBytes: number;
}

export interface GlobalServicePatch {
  replicas?: number;
  cpuLimitMilli?: number;
  memoryLimitBytes?: number;
  nodeName?: string;
  imageTag?: string;
  ingressHost?: string;
  tlsEnabled?: boolean;
}

export interface CreateGlobalServiceReq {
  name: string;
  chart: string;
  chartVersion: string;
  serviceGroup?: string;
  icon?: string;
}

export function fetchGlobalServices(): Promise<GlobalService[]> {
  return request<GlobalService[]>('/api/v1/global/services');
}

export function createGlobalService(body: CreateGlobalServiceReq): Promise<GlobalService> {
  return request<GlobalService>('/api/v1/global/services', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function patchGlobalService(name: string, body: GlobalServicePatch): Promise<GlobalService> {
  return request<GlobalService>(`/api/v1/global/services/${name}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function deleteGlobalService(name: string): Promise<void> {
  return request<void>(`/api/v1/global/services/${name}`, { method: 'DELETE' });
}

export function restartGlobalService(name: string): Promise<void> {
  return request<void>(`/api/v1/global/services/${name}/restart`, { method: 'POST' });
}

// fetchGlobalServiceImageTags lists selectable image versions for a global
// service. Same contract as fetchServiceImageTags (always 200, degrades to
// {supported:false}).
export function fetchGlobalServiceImageTags(name: string): Promise<ImageTags> {
  return request<ImageTags>(`/api/v1/global/services/${encodeURIComponent(name)}/image-tags`);
}

export function fetchGlobalServiceLogs(name: string): Promise<{ logs: string }> {
  return request<{ logs: string }>(`/api/v1/global/services/${name}/logs`);
}

// ── Storage servers ─────────────────────────────────────────────────────────
// The MinIO instances a managed bucket can be provisioned on. One is usually
// configured through the environment and needs no row here; these are the
// additional ones an operator registers.

export interface StorageServer {
  id: string;
  name: string;
  endpoint: string;
  publicEndpoint: string;
  region: string;
  rootUser: string;
  note: string;
  /** Managed buckets living on it — non-zero means it cannot be removed. */
  buckets: number;
  createdAt: string;
  updatedAt: string;
}

/** The root password only ever travels inwards; no response carries it back. */
export interface StorageServerInput {
  name: string;
  endpoint: string;
  publicEndpoint?: string;
  region?: string;
  rootUser: string;
  rootPassword?: string;
  note?: string;
}

export function fetchStorageServers(): Promise<StorageServer[]> {
  return request<StorageServer[]>('/api/v1/minio-servers');
}

export function createStorageServer(input: StorageServerInput): Promise<StorageServer> {
  return request<StorageServer>('/api/v1/minio-servers', { method: 'POST', body: JSON.stringify(input) });
}

export function updateStorageServer(id: string, input: Partial<StorageServerInput>): Promise<StorageServer> {
  return request<StorageServer>(`/api/v1/minio-servers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteStorageServer(id: string): Promise<void> {
  return request<void>(`/api/v1/minio-servers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Asks the server to contact the storage server now. Never throws for an
 *  unreachable server — that is a result, not a request failure. */
export function testStorageServer(id: string): Promise<{ ok: boolean; error?: string }> {
  return request<{ ok: boolean; error?: string }>(`/api/v1/minio-servers/${encodeURIComponent(id)}/test`, {
    method: 'POST',
  });
}
